import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Box, Button, keyframes, Typography } from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import { AiEvent, AiEventType, ToolResultEvent } from "../../types/ai-event";
import type { AiHistoryState, QueuedAiMessage } from "../../slices/ui";
import { ChatLifecyclePhase, chatPhase } from "../../connection/chatPhase";
import { TextBlock } from "./events/TextBlock";
import { ToolCallGroup } from "./events/ToolCallGroup";
import { ApprovalPrompt } from "./events/ApprovalPrompt";
import { QuestionCard } from "./events/QuestionCard";
import { StatusIndicator } from "./events/StatusIndicator";
import { TypingIndicator } from "./events/TypingIndicator";
import { AgentRow } from "./events/AgentRow";
import { QueuedMessage } from "./events/QueuedMessage";
import { CasLoader } from "../brand/CasLoader";
import { groupToolRows, type EventRow, type Row } from "./groupToolRows";
import { useStickToBottom } from "./stickToBottom";

// Events that render on Cas's side of the thread (SMS-style, beside her avatar).
const AGENT_SIDE = new Set<AiEventType>([
  AiEventType.AGENT_MESSAGE,
  AiEventType.TOOL_USE,
  AiEventType.QUESTION,
  AiEventType.APPROVAL_REQUEST,
]);
// Events that render nothing in the log (tool results fold into their card; running/idle/
// progress/provisioning drive the typing receipt). They are not list rows at all: react-virtuoso
// warns on zero-size items.
const INVISIBLE = new Set<AiEventType>([
  AiEventType.TOOL_RESULT,
  AiEventType.STATUS_RUNNING,
  AiEventType.STATUS_IDLE,
  AiEventType.STATUS_PROGRESS,
  AiEventType.STATUS_PROVISIONING,
  AiEventType.BACKGROUND_TASKS,   // drawn by BackgroundTasksBar above the composer, not in the log
]);

interface ListContext {
  history?: AiHistoryState;
  onLoadOlder?: () => void;
  // The typing receipt is the list's Footer: directly under the last message, like a chat message
  // (U.7), and never inside the item list react-virtuoso can keep visibility: hidden (A.27).
  typing?: boolean;
  typingText?: string;
  statusText?: string;
  // Messages waiting for the running turn to end (state.ui.aiQueued). They are drawn after the typing receipt,
  // nearest the composer, in send order; they are never rows, so replay and the row keys do not see them.
  queued?: QueuedAiMessage[];
  onCancelQueued?: (id: string) => void;
}

// react-virtuoso keeps the row on screen in place when older rows are prepended if firstItemIndex
// drops by the number of rows added; it "should be a positive number" (dist/index.d.ts), so it
// starts high enough for any number of 20-row History Pages.
const FIRST_ITEM_INDEX = 1000000;
// Constant for a mounted list in every state (loading / error / beginning / nothing), so the header
// never changes the height above the first row and the row on screen never moves when a page lands.
// A thread with no History Page (a new chat) has production's 12 px top padding instead; `loaded`
// only changes with a new transcript, which remounts the list (key={epoch}).
const HISTORY_HEADER_HEIGHT = 32;
const LIST_TOP_PADDING = 12;
const LIST_BOTTOM_PADDING = 12;

// A row's React/virtuoso key: stable across prepends (never the array index).
function baseRowKey(event: AiEvent): string {
  switch (event.type) {
    case AiEventType.USER_MESSAGE:
      return event.id ? `user:${event.id}` : `user-local:${event.timestamp}`;
    case AiEventType.AGENT_MESSAGE:
      return event.id ? `agent:${event.id}` : `agent-local:${event.timestamp}`;
    case AiEventType.TOOL_USE:
      return `tool:${event.id}`;
    case AiEventType.QUESTION:
      return `question:${event.requestID}`;
    case AiEventType.APPROVAL_REQUEST:
      return `approval:${event.id}`;
    default:
      return `${event.type}:${event.timestamp}`;
  }
}

const HistoryHeader: React.FC<{ context: ListContext }> = ({ context }) => {
  const history = context.history;
  return (
    <Box
      data-testid="history-header"
      sx={{
        height: history?.loaded ? HISTORY_HEADER_HEIGHT : LIST_TOP_PADDING,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
      }}
    >
      {history?.loading ? (
        <>
          <CasLoader size={16} label="Loading earlier messages" />
          <Typography variant="caption" color="text.secondary">
            Loading earlier messages…
          </Typography>
        </>
      ) : history?.error ? (
        <>
          <Typography data-testid="history-error" variant="caption" color="error">
            Couldn't load earlier messages —
          </Typography>
          <Button data-testid="history-retry" size="small" onClick={() => context.onLoadOlder?.()}>
            Retry
          </Button>
        </>
      ) : history?.loaded && !history.hasMore ? (
        <Typography data-testid="history-start" variant="caption" color="text.secondary">
          Beginning of conversation
        </Typography>
      ) : null}
    </Box>
  );
};

const NO_CANCEL = () => undefined;

const ListFooter: React.FC<{ context: ListContext }> = ({ context }) => (
  <Box sx={{ pb: `${LIST_BOTTOM_PADDING}px` }}>
    {context.typing ? (
      <Box sx={{ display: "flex", flexDirection: "column", px: 1.5, pb: 0.5 }}>
        {React.createElement(AgentRow, {
          showAvatar: true,
          children: <TypingIndicator text={context.typingText} />,
        })}
      </Box>
    ) : context.statusText ? (
      <Box sx={{ px: 1.5, pb: 0.5 }}>
        <Typography data-testid="chat-status-footer" variant="caption" color="text.secondary">
          {context.statusText}
        </Typography>
      </Box>
    ) : null}
    {context.queued?.map((message) => (
      <Box key={message.id} sx={{ display: "flex", flexDirection: "column", px: 1.5, pb: 0.5 }}>
        <QueuedMessage message={message} onCancel={context.onCancelQueued ?? NO_CANCEL} />
      </Box>
    ))}
  </Box>
);

// The label shimmers in TypingIndicator; the skeleton bubbles shimmer the same way (spec Decision 3:
// "shimmer bubbles consistent with TypingIndicator"), so a resuming chat reads as loading, not as
// three static grey boxes.
const shimmer = keyframes`
  from { background-position: 100% 0; }
  to { background-position: 0 0; }
`;

const SkeletonBubble: React.FC<{ index: number }> = ({ index }) => (
  <Box
    data-testid="skeleton-bubble"
    sx={{
      mb: 0.75,
      height: 36 - index * 6,
      width: `${70 - index * 10}%`,
      borderRadius: 2,
      backgroundImage: (theme) =>
        `linear-gradient(90deg, ${theme.palette.action.hover} 0%, ${theme.palette.action.hover} 40%, ${theme.palette.action.selected} 50%, ${theme.palette.action.hover} 60%, ${theme.palette.action.hover} 100%)`,
      backgroundSize: "200% 100%",
      animation: `${shimmer} 1.6s linear infinite`,
      "@media (prefers-reduced-motion: reduce)": {
        animation: "none",
        backgroundImage: "none",
        backgroundColor: "action.hover",
      },
    }}
  />
);

const LIST_COMPONENTS = { Header: HistoryHeader, Footer: ListFooter };

interface AiPanelMessagesProps {
  events: AiEvent[];
  isRunning: boolean;
  onApprove: (id: string) => void;
  onDeny: (id: string) => void;
  onQuestionReply: (requestID: string, answers: string[][]) => void;
  // History Page state and the load-older action (agent-chat-speed Decision 1). Absent → no paging.
  history?: AiHistoryState;
  onLoadOlder?: () => void;
  // The single derived chat lifecycle phase (spec Decision 2). Absent → only isRunning is used.
  lifecycle?: ChatLifecyclePhase;
  activeSessionId?: string | null;
  // The queue of messages sent during a turn and the cancel action for one of them (Redux, via useAiQueue).
  queued?: QueuedAiMessage[];
  onCancelQueued?: (id: string) => void;
  // The Background button of a running Bash call (ToolCallCard): called with the call's id.
  onBackground?: (toolUseId: string) => void;
}

export const AiPanelMessages: React.FC<AiPanelMessagesProps> = ({
  events,
  isRunning,
  onApprove,
  onDeny,
  onQuestionReply,
  history,
  onLoadOlder,
  lifecycle,
  activeSessionId,
  queued,
  onCancelQueued,
  onBackground,
}) => {
  // U.5 — the typing receipt's ONE stable label, swapped in place: a progress line, "Starting
  // workspace…", "Thinking…", and nothing once Cas has produced output (chatPhase.ts).
  const typingText = useMemo(() => chatPhase(events).label, [events]);

  // The lifecycle phase decides whether Cas is mid-run; isRunning only stands in when no phase was
  // passed (an AiPanelMessages mounted without the lifecycle).
  const running = lifecycle === undefined ? isRunning : lifecycle === "running";
  const statusText =
    lifecycle === "connecting" ? "Connecting…"
    : lifecycle === "reconnecting" ? "Reconnecting…"
    : lifecycle === "offline" ? "Disconnected"
    : lifecycle === "resuming" ? "Resuming chat…"
    : lifecycle === "loading-history" ? "Loading chat…"
    : undefined;
  const loadingCaption =
    lifecycle === "resuming" ? "Resuming chat…"
    : lifecycle === "loading-history" ? "Loading chat…"
    : null;

  // One pass per events change (Decision 3) instead of a scan per row: the visible rows, the tool
  // results by call id, and the number of the user's own unsent-from-server bubbles.
  const { rows, toolResults, localSends } = useMemo(() => {
    const toolResults = new Map<string, ToolResultEvent>();
    const eventRows: EventRow[] = [];
    const seen = new Map<string, number>();
    let localSends = 0;
    events.forEach((event, index) => {
      if (event.type === AiEventType.TOOL_RESULT && !toolResults.has(event.toolUseId)) {
        toolResults.set(event.toolUseId, event);
      }
      if (event.type === AiEventType.USER_MESSAGE && !event.id) localSends += 1;
      if (INVISIBLE.has(event.type)) return;
      const base = baseRowKey(event);
      const n = seen.get(base) ?? 0;
      seen.set(base, n + 1);
      eventRows.push({ kind: "event", key: n === 0 ? base : `${base}#${n}`, event, index, showAvatar: false });
    });
    // A run of consecutive tool calls folds into one row (spec D3).
    const rows: Row[] = groupToolRows(eventRows);
    // SMS convention: Cas's avatar is drawn once per run of her rows, on the last one. While she is
    // running, the typing receipt ends the run, so it carries the avatar instead.
    let nextIsAgentSide = running;
    for (let r = rows.length - 1; r >= 0; r--) {
      const row = rows[r];
      const agentSide = row.kind === "toolGroup" || AGENT_SIDE.has(row.event.type);
      row.showAvatar = agentSide && !nextIsAgentSide;
      nextIsAgentSide = agentSide;
    }
    return { rows, toolResults, localSends };
  }, [events, running]);

  // Prepend vs. new transcript. When the first event row changes and the old one is further down,
  // older rows were prepended: lower firstItemIndex by that many (the row on screen stays put).
  // Otherwise a new transcript arrived (open, resume, clear): remount the list (key) so it opens AT
  // THE BOTTOM via initialTopMostItemIndex — no smooth scroll through the history. Idempotent, so
  // StrictMode's double-invoked useMemo computes the same value.
  const wrapRef = useRef<HTMLDivElement>(null);
  const pendingAnchor = useRef<{ index: number; offset: number } | null>(null);
  const listState = useRef({ firstKey: null as string | null, firstIndex: FIRST_ITEM_INDEX, epoch: 0 });
  const { firstItemIndex, epoch } = useMemo(() => {
    const st = listState.current;
    const firstKey = rows.length > 0 ? rows[0].key : null;
    if (firstKey !== st.firstKey) {
      const k = st.firstKey === null ? -1 : rows.findIndex((r) => r.key === st.firstKey);
      if (k > 0) {
        // Remember where the old first row sits (render still sees the pre-prepend DOM), so the
        // layout effect below can put it back once the new rows are measured.
        const scroller = wrapRef.current?.querySelector<HTMLElement>("[data-virtuoso-scroller]");
        const item = scroller?.querySelector<HTMLElement>(`[data-item-index="${st.firstIndex}"]`);
        if (scroller && item) {
          pendingAnchor.current = {
            index: st.firstIndex,
            offset: item.getBoundingClientRect().top - scroller.getBoundingClientRect().top,
          };
        }
        st.firstIndex -= k;
      } else if (st.firstKey === null && firstKey !== null && firstKey.startsWith("user-local:")) {
        // The user's own first message in an empty thread is an append, not a new transcript: no
        // remount, so the bubble shows in the same frame (spec Data flow, "≤ 1 frame"; A.27).
      } else {
        st.firstIndex = FIRST_ITEM_INDEX;
        st.epoch += 1;
      }
      st.firstKey = firstKey;
    }
    return { firstItemIndex: st.firstIndex, epoch: st.epoch };
  }, [rows]);

  // Virtuoso sizes prepended rows at its last known row height and, when the list was at index 0
  // before and after, skips the upward compensation (react-virtuoso dist/index.mjs ~2130), so once
  // the real heights are measured the row on screen has drifted. Put it back: for a short while
  // after the prepend, scroll by the anchor row's offset error until it is stable. A wheel/touch
  // from the reader ends the correction.
  useLayoutEffect(() => {
    const anchor = pendingAnchor.current;
    pendingAnchor.current = null;
    const scroller = wrapRef.current?.querySelector<HTMLElement>("[data-virtuoso-scroller]");
    if (!anchor || !scroller) return;
    let stopped = false;
    let stableFrames = 0;
    let frames = 0;
    const stop = () => { stopped = true; };
    scroller.addEventListener("wheel", stop, { passive: true, once: true });
    scroller.addEventListener("touchstart", stop, { passive: true, once: true });
    const tick = () => {
      if (stopped || frames++ > 120) return cleanup();
      const item = scroller.querySelector<HTMLElement>(`[data-item-index="${anchor.index}"]`);
      if (item) {
        const err = item.getBoundingClientRect().top - scroller.getBoundingClientRect().top - anchor.offset;
        if (Math.abs(err) > 0.5) {
          scroller.scrollTop += err;
          stableFrames = 0;
        } else {
          stableFrames++;
        }
      }
      if (stableFrames >= 20) return cleanup();
      raf = requestAnimationFrame(tick);
    };
    const cleanup = () => {
      scroller.removeEventListener("wheel", stop);
      scroller.removeEventListener("touchstart", stop);
    };
    let raf = requestAnimationFrame(tick);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      cleanup();
    };
  }, [firstItemIndex]);

  // The skeleton and the empty state are not lists: useStickToBottom has no scroller to find then, and looks for
  // it again when the list mounts or is remounted (key={epoch}).
  const showSkeleton = !!loadingCaption && rows.length === 0;
  const showEmpty = !showSkeleton && lifecycle === "idle" && activeSessionId === null && rows.length === 0;
  // Stuck to the bottom (Terms: Stuck, Pin): live output follows until the reader scrolls up, and growth of any
  // kind keeps it pinned in the same frame. Virtuoso's followOutput is off: it fires only when the row count
  // changes, and a streamed reply grows one row in place.
  const { stick } = useStickToBottom(wrapRef, showSkeleton || showEmpty ? -1 : epoch);

  // The user's own new message is always brought into view, even when scrolled up in history.
  const sentRef = useRef(localSends);
  useEffect(() => {
    if (localSends > sentRef.current) stick();
    sentRef.current = localSends;
  }, [localSends, stick]);

  // A newly queued message is brought into view like a sent one. It sits in the footer, below the last row.
  const queuedCount = queued?.length ?? 0;
  const queuedRef = useRef(queuedCount);
  useEffect(() => {
    if (queuedCount > queuedRef.current) stick();
    queuedRef.current = queuedCount;
  }, [queuedCount, stick]);

  const onStartReached = useCallback(() => {
    if (history && history.hasMore && !history.loading && !history.error) onLoadOlder?.();
  }, [history, onLoadOlder]);

  // Read once per mount (key={epoch}). A list that mounts empty has no last row to open at; given
  // "LAST" it keeps its first rows visibility: hidden until it has scrolled there (react-virtuoso
  // dist/index.mjs:1178,2508), which hid a new chat's first message for 1.2 s (A.27).
  const mountedWithRows = useMemo(() => rows.length > 0, [epoch]);
  const context = useMemo<ListContext>(
    () => ({ history, onLoadOlder, typing: running, typingText, statusText, queued, onCancelQueued }),
    [history, onLoadOlder, running, typingText, statusText, queued, onCancelQueued]
  );
  const computeItemKey = useCallback((_index: number, row: Row) => row.key, []);
  // A tool call is running while nothing but a task list came after it: session.background_tasks arrives beside the call, not as its successor.
  let lastIndex = events.length - 1;
  while (lastIndex > 0 && events[lastIndex].type === AiEventType.BACKGROUND_TASKS) lastIndex--;

  const renderRow = useCallback(
    (_index: number, row: Row) => {
      let body: React.ReactNode = null;
      if (row.kind === "toolGroup") {
        body = (
          <AgentRow showAvatar={row.showAvatar}>
            <ToolCallGroup calls={row.calls} toolResults={toolResults} lastIndex={lastIndex} onBackground={onBackground} />
          </AgentRow>
        );
      } else {
        const { event, showAvatar } = row;
        switch (event.type) {
          case AiEventType.USER_MESSAGE:
            body = <TextBlock event={event} />;
            break;
          case AiEventType.AGENT_MESSAGE:
            body = (
              <AgentRow showAvatar={showAvatar}>
                <TextBlock event={event} />
              </AgentRow>
            );
            break;
          case AiEventType.APPROVAL_REQUEST:
            body = (
              <AgentRow showAvatar={showAvatar}>
                <ApprovalPrompt event={event} onApprove={onApprove} onDeny={onDeny} />
              </AgentRow>
            );
            break;
          case AiEventType.QUESTION:
            body = (
              <AgentRow showAvatar={showAvatar}>
                <QuestionCard event={event} onReply={onQuestionReply} />
              </AgentRow>
            );
            break;
          case AiEventType.STATUS_TERMINATED:
          case AiEventType.SESSION_ERROR:
          case AiEventType.CONNECTION_ERROR:
            body = <StatusIndicator event={event} />;
            break;
          default:
            body = null;
        }
      }
      // Each row is its own flex column so a user bubble's alignSelf: flex-end still right-aligns.
      return <Box sx={{ display: "flex", flexDirection: "column", px: 1.5, pb: 0.5 }}>{body}</Box>;
    },
    [toolResults, lastIndex, onApprove, onDeny, onQuestionReply, onBackground]
  );

  if (showSkeleton) {
    return (
      <Box ref={wrapRef} data-testid="ai-messages" sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 1.5 }}>
        <Box data-testid="thread-skeleton">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <CasLoader size={20} label={loadingCaption} />
            <Typography data-testid="chat-status-footer" variant="caption" color="text.secondary">
              {loadingCaption}
            </Typography>
          </Box>
          {[0, 1, 2].map((i) => (
            <SkeletonBubble key={i} index={i} />
          ))}
        </Box>
      </Box>
    );
  }
  if (showEmpty) {
    return (
      <Box ref={wrapRef} data-testid="ai-messages" sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box data-testid="chat-empty-state" sx={{ m: "auto", px: 3, textAlign: "center" }}>
          <Typography variant="subtitle2">Start a conversation with Cas</Typography>
          <Typography variant="caption" color="text.secondary">Send your first message below.</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={wrapRef} data-testid="ai-messages" sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Virtuoso<Row, ListContext>
        key={epoch}
        style={{ flex: 1, minHeight: 0, overscrollBehavior: "contain" }}
        data={rows}
        context={context}
        components={LIST_COMPONENTS}
        firstItemIndex={firstItemIndex}
        initialTopMostItemIndex={mountedWithRows ? { index: "LAST", align: "end" } : 0}
        computeItemKey={computeItemKey}
        itemContent={renderRow}
        increaseViewportBy={{ top: 400, bottom: 200 }}
        startReached={onStartReached}
      />
    </Box>
  );
};
